"use client";

import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import OrderTableItem from "./OrderTableItem";
import OrderDetailsModal from "./OrderDetailsModal";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const [maxTotal, setMaxTotal] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);

  document.title = "Orders | Tafaseel Admin";

  const statusOptions = [
    { label: "All Statuses", value: "" },
    { label: "Pending", value: "pending" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Processing", value: "processing" },
    { label: "Shipped", value: "shipped" },
    { label: "Delivered", value: "delivered" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Refunded", value: "refunded" },
  ];

  const sortOptions = [
    { label: "Date Created", value: "created_at" },
    { label: "Total Amount", value: "total_amount" },
    { label: "Status", value: "status" },
    { label: "Order Number", value: "order_number" },
  ];

  useEffect(() => {
    fetchOrders();
  }, [
    currentPage,
    searchQuery,
    statusFilter,
    sortBy,
    sortOrder,
    dateFrom,
    dateTo,
    minTotal,
    maxTotal,
  ]);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        per_page: 15,
        search: searchQuery,
        status: statusFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        date_from: dateFrom,
        date_to: dateTo,
        min_total: minTotal,
        max_total: maxTotal,
      };

      // Remove empty parameters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/orders", {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setOrders(response.data.data);
        setTotalPages(response.data.meta?.last_page || 1);
        setTotalOrders(response.data.meta?.total || response.data.data.length);
        setFilteredData([]);
      } else {
        toast.error("Failed to fetch orders", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Error fetching orders", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchOrders();
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);

    if (query === "" && statusFilter === "") {
      setFilteredData([]);
      return;
    }

    let filtered = orders;

    // Apply search filter
    if (query !== "") {
      filtered = filtered.filter(
        (order) =>
          order.order_number?.toLowerCase().includes(query) ||
          order.user?.name?.toLowerCase().includes(query) ||
          order.user?.email?.toLowerCase().includes(query) ||
          order.id.toString().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== "") {
      filtered = filtered.filter((order) => order.status === statusFilter);
    }

    setFilteredData(filtered);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);

    if (status === "" && searchQuery === "") {
      setFilteredData([]);
      return;
    }

    let filtered = orders;

    // Apply status filter
    if (status !== "") {
      filtered = filtered.filter((order) => order.status === status);
    }

    // Apply search filter
    if (searchQuery !== "") {
      filtered = filtered.filter(
        (order) =>
          order.order_number?.toLowerCase().includes(searchQuery) ||
          order.user?.name?.toLowerCase().includes(searchQuery) ||
          order.user?.email?.toLowerCase().includes(searchQuery) ||
          order.id.toString().includes(searchQuery)
      );
    }

    setFilteredData(filtered);
  };

  const handleViewOrder = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.get(`/admin/orders/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setSelectedOrder(response.data.data);
        setShowDetailsModal(true);
      } else {
        toast.error("Failed to fetch order details", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Error fetching order details", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleDeleteOrder = (orderId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this order? This action cannot be undone."
      )
    ) {
      return;
    }

    confirmDeleteOrder(orderId);
  };

  const confirmDeleteOrder = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.delete(`/admin/orders/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        toast.success("Order deleted successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
        fetchOrders();
      } else {
        toast.error("Failed to delete order", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Error deleting order", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/orders/${orderId}/status`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Order status updated successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
        fetchOrders();
        if (selectedOrder && selectedOrder.id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus });
        }
      } else {
        toast.error("Failed to update order status", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Error updating order status", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setShowFiltersModal(false);
    fetchOrders();
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setMinTotal("");
    setMaxTotal("");
    setStatusFilter("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Get order counts by status
  const getStatusCounts = () => {
    const counts = {
      all: orders.length,
      pending: orders.filter((o) => o.status === "pending").length,
      confirmed: orders.filter((o) => o.status === "confirmed").length,
      processing: orders.filter((o) => o.status === "processing").length,
      shipped: orders.filter((o) => o.status === "shipped").length,
      delivered: orders.filter((o) => o.status === "delivered").length,
      cancelled: orders.filter((o) => o.status === "cancelled").length,
      refunded: orders.filter((o) => o.status === "refunded").length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Pagination logic
  const dataToShow = filteredData.length > 0 ? filteredData : orders;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataToShow.slice(indexOfFirstItem, indexOfLastItem);
  const totalPagesCalc = Math.ceil(dataToShow.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPagesCalc) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 10;
    const maxVisiblePages = Math.min(totalPagesCalc, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPagesCalc, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Tafaseel Admin" breadcrumbItem="Orders" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="orderList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            className="refresh-btn"
                            onClick={handleRefresh}
                            disabled={loading}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            {loading ? "Loading..." : "Refresh"}
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end gap-2">
                          <div className="status-filter">
                            <select
                              className="form-select"
                              value={statusFilter}
                              onChange={(e) =>
                                handleStatusFilter(e.target.value)
                              }
                            >
                              {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="search-box">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search orders..."
                              value={searchQuery}
                              onChange={handleSearch}
                            />
                          </div>
                          <Button
                            color="info"
                            onClick={() => setShowFiltersModal(true)}
                          >
                            <i className="ri-filter-line me-1"></i>
                            Filters
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {/* Status Summary Cards */}
                    <Row className="mb-3">
                      <Col>
                        <div className="d-flex gap-3 flex-wrap">
                          <div className="status-card p-2 border rounded">
                            <small className="text-muted">Total</small>
                            <div className="fw-bold">{statusCounts.all}</div>
                          </div>
                          <div className="status-card p-2 border rounded border-warning">
                            <small className="text-warning">Pending</small>
                            <div className="fw-bold text-warning">
                              {statusCounts.pending}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-success">
                            <small className="text-success">Delivered</small>
                            <div className="fw-bold text-success">
                              {statusCounts.delivered}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-danger">
                            <small className="text-danger">Cancelled</small>
                            <div className="fw-bold text-danger">
                              {statusCounts.cancelled}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      {loading ? (
                        <div className="text-center p-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading orders...</p>
                        </div>
                      ) : (
                        <table
                          className="table align-middle table-nowrap"
                          id="orderTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th className="sort" data-sort="order_id">
                                Order ID
                              </th>
                              <th className="sort" data-sort="customer">
                                Customer
                              </th>
                              <th className="sort" data-sort="items">
                                Items
                              </th>
                              <th className="sort" data-sort="total">
                                Total
                              </th>
                              <th className="sort" data-sort="status">
                                Status
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {currentItems.length > 0 ? (
                              currentItems.map((order) => (
                                <OrderTableItem
                                  key={order.id}
                                  order={order}
                                  onView={handleViewOrder}
                                  onDelete={handleDeleteOrder}
                                  onUpdateStatus={handleUpdateOrderStatus}
                                />
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center p-4">
                                  <div className="text-center">
                                    <lord-icon
                                      src="https://cdn.lordicon.com/msoeawqm.json"
                                      trigger="loop"
                                      colors="primary:#121331,secondary:#08a88a"
                                      style={{ width: "75px", height: "75px" }}
                                    ></lord-icon>
                                    <h5 className="mt-2">No Orders Found</h5>
                                    <p className="text-muted mb-0">
                                      {searchQuery || statusFilter
                                        ? `No orders found matching your criteria`
                                        : "No orders available at the moment."}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Pagination */}
                    {totalPagesCalc > 1 && (
                      <div className="d-flex justify-content-center">
                        <div className="pagination-wrap hstack gap-2">
                          <div className="d-flex justify-content-end">
                            <div
                              className="btn-group me-2"
                              role="group"
                              aria-label="Pagination"
                            >
                              <button
                                className="page-item pagination-prev btn btn-primary"
                                onClick={prevPage}
                                type="button"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>

                              {getPageNumbers().map((pageNumber) => (
                                <button
                                  type="button"
                                  className={`btn ${
                                    pageNumber === currentPage
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  key={pageNumber}
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}

                              <button
                                disabled={currentPage === totalPagesCalc}
                                className="page-item pagination-next btn btn-primary"
                                type="button"
                                onClick={nextPage}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show results info */}
                    {dataToShow.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Showing {indexOfFirstItem + 1} to{" "}
                          {Math.min(indexOfLastItem, dataToShow.length)} of{" "}
                          {dataToShow.length} orders
                        </div>
                        <div className="text-muted">
                          Page {currentPage} of {totalPagesCalc}
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Order Details Modal */}
          {showDetailsModal && (
            <OrderDetailsModal
              visible={showDetailsModal}
              order={selectedOrder}
              onClose={() => setShowDetailsModal(false)}
              onUpdateStatus={handleUpdateOrderStatus}
            />
          )}

          {/* Advanced Filters Modal */}
          <Modal
            isOpen={showFiltersModal}
            toggle={() => setShowFiltersModal(false)}
            centered
            size="lg"
          >
            <ModalHeader toggle={() => setShowFiltersModal(false)}>
              <i className="ri-filter-line me-2"></i>
              Advanced Filters
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label">Date From</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label">Date To</label>
                    <input
                      type="date"
                      className="form-control"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label">Min Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0.00"
                      value={minTotal}
                      onChange={(e) => setMinTotal(e.target.value)}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label">Max Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0.00"
                      value={maxTotal}
                      onChange={(e) => setMaxTotal(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={clearFilters}>
                Clear All
              </Button>
              <Button color="primary" onClick={applyFilters}>
                Apply Filters
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Orders;
